import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';

import { environment } from 'environments/environment';

import { IPaginatedListViewSearchModel, ObjectCreatedResponseModel, PaginatedModel } from '../models/common.models';
import {
  ICreateSiteLocationModel,
  ISiteLocationDetailsModel,
  ISiteLocationListItemModel,
  ISiteLocationLookupItemModel,
  ISiteLocationStorageTypes,
  IUpdateSiteLocationModel,
} from '../models/site-location.models';
import { FilterParameterService } from './filter-parameter.service';
import { FilterState } from './filter-state.service';

@Injectable({
  providedIn: 'root',
})
export class SiteLocationService {
  private searchEvent = new Subject<any>();

  searchEvent$ = this.searchEvent.asObservable();

  private get baseApiUrl(): string {
    return environment.storageManagementUrl;
  }

  constructor(
    private http: HttpClient,
    private filterParameterService: FilterParameterService,
  ) {}

  getSiteLocationsV1(
    siteId: number,
    searchModel: IPaginatedListViewSearchModel,
  ): Observable<PaginatedModel<ISiteLocationListItemModel>> {
    const url = `${this.baseApiUrl}/v1/site/${siteId}/locations`;
    const params = new HttpParams()
      .append('filterText', searchModel.searchText)
      .append('orderByColumn', searchModel.orderByColumn)
      .append('orderByDirection', searchModel.orderByDirection)
      .append('page', searchModel.page.toString())
      .append('pageSize', searchModel.pageSize.toString());

    return this.http.get<PaginatedModel<ISiteLocationListItemModel>>(url, {
      params,
    });
  }

  getSiteLocationsLookupV1(
    siteId: number,
    filterText: string,
    isIncludeStorageType = true,
    isIncludeReceivingType = true,
    isIncludeDispatchType = true,
  ): Observable<PaginatedModel<ISiteLocationLookupItemModel>> {
    const url = `${this.baseApiUrl}/v1/site/${siteId}/locations/lookup`;
    const params = new HttpParams()
      .append('filterText', filterText)
      .append('isIncludeStorageType', isIncludeStorageType.toString())
      .append('isIncludeReceivingType', isIncludeReceivingType.toString())
      .append('isIncludeDispatchType', isIncludeDispatchType.toString());
    return this.http.get<PaginatedModel<ISiteLocationLookupItemModel>>(url, {
      params,
    });
  }

  getManySitesLocationsLookupV1(
    filterText: string,
    isIncludeStorageType = true,
    isIncludeReceivingType = true,
    isIncludeDispatchType = true,
    filters?: FilterState[],
  ): Observable<PaginatedModel<ISiteLocationLookupItemModel>> {
    const url = `${this.baseApiUrl}/v1/site/locations/lookup`;

    let params = new HttpParams()
      .append('filterText', filterText)
      .append('isIncludeStorageType', isIncludeStorageType.toString())
      .append('isIncludeReceivingType', isIncludeReceivingType.toString())
      .append('isIncludeDispatchType', isIncludeDispatchType.toString());

    params = this.filterParameterService.getFilterParametersWithIndividualDateParams(params, filters);

    return this.http.get<PaginatedModel<ISiteLocationLookupItemModel>>(url, {
      params,
    });
  }

  getSiteDefaultReceivingLocationV1(siteId: number): Observable<ISiteLocationLookupItemModel> {
    const url = `${this.baseApiUrl}/v1/site/${siteId}/locations/defaultReceivingLocation`;
    return this.http.get<ISiteLocationLookupItemModel>(url);
  }

  getSiteLocationDetailsV1(locationId: number): Observable<ISiteLocationDetailsModel> {
    const url = `${this.baseApiUrl}/v1/site/locations/${locationId}`;
    return this.http.get<ISiteLocationDetailsModel>(url, {});
  }

  getSiteLocationDetailsByLabelCodeV1(labelCode: string): Observable<ISiteLocationDetailsModel> {
    const url = `${this.baseApiUrl}/v1/site/locations/${labelCode}`;
    return this.http.get<ISiteLocationDetailsModel>(url);
  }

  createSiteLocationV1(siteId: number, model: ICreateSiteLocationModel): Observable<ObjectCreatedResponseModel> {
    const url = `${this.baseApiUrl}/v1/site/${siteId}/locations`;
    return this.http.post<ObjectCreatedResponseModel>(url, model);
  }

  updateSiteLocationV1(locationId: number, model: IUpdateSiteLocationModel): Observable<void> {
    const url = `${this.baseApiUrl}/v1/site/locations/${locationId}`;
    return this.http.put<void>(url, model);
  }

  deleteSiteLocationV1(locationId: number): Observable<void> {
    const url = `${this.baseApiUrl}/v1/site/locations/${locationId}`;
    return this.http.delete<void>(url, {});
  }

  getLocationStorageTypes(location: ISiteLocationStorageTypes): string[] {
    if (location) {
      return [
        location.isStorageType && 'Storage',
        location.isReceivingType && 'Receiving',
        location.isDispatchType && 'Dispatch',
      ].filter(Boolean);
    }
    return [];
  }

  getPrintLabelUrl(): string {
    return `${environment.storageManagementUrl}/v1/Site/locations/labels`;
  }

  bulkAddLocations(siteId: number, locations: ICreateSiteLocationModel[]) {
    const url = `${this.baseApiUrl}/v1/site/${siteId}/locations/addBulk`;
    return this.http.post<number[]>(url, locations);
  }
}
